import React, { useEffect, useRef } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Button } from '@/components/common/Button';
import BentoCard from '@/components/common/Card/BentoCard';

// Define types for our bento items
interface BentoItem {
  id: string;
  title: string;
  description: string;
  iconPath: string;
}

interface Bento8Props {
  tag: string;
  title: string;
  description: string;
  bentoItems: BentoItem[];
  demoSection?: {
    title: string;
    imageSrc: string;
    imageAlt: string;
    ctaText: string;
    ctaHref: string;
  };
}

const Bento8: React.FC<Bento8Props> = ({ tag, title, description, bentoItems, demoSection }) => {
  // Create ref for video element
  const videoRef = useRef<HTMLVideoElement>(null);

  // First three items for the first row
  const firstRowItems = bentoItems.slice(0, 3);
  // Remaining items
  const remainingItems = bentoItems.slice(3);

  // Handle video playback when component mounts
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Function to handle playback
    const attemptPlay = () => {
      video.play().catch(error => {
        console.log("Autoplay prevented:", error);
        // Add a play button or other UI indication that user needs to interact
      });
    };

    // Try to play the video
    attemptPlay();

    // Add event listeners for visibility changes and focus
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        attemptPlay();
      } else {
        video.pause();
      }
    });

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', () => {});
    };
  }, []);

  const handleLearnMoreClick = () => {
    window.open('https://cal.com/cobalt/welcome-to-cobalt', '_blank');
  };

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-primary-600 tracking-wider mb-4">
            {tag}
          </span>
          <div className="flex flex-col md:flex-row md:items-center md:justify-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 md:mb-0">
              {title}
            </h2>
          </div>
          <p className="text-lg text-gray-600">
            {description}
          </p>
        </div>

        {/* First row - Three cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {firstRowItems.map((item) => (
            <BentoCard
              key={item.id}
              title={item.title}
              description={item.description}
              iconPath={item.iconPath}
            />
          ))}
        </div>

        {/* Main content container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left column - Enterprise Connectors and AI-Readiness stacked */}
          <div className="flex flex-col gap-6">
            {/* First stacked item */}
            {remainingItems[0] && (
              <BentoCard
                title={remainingItems[0].title}
                description={remainingItems[0].description}
                iconPath={remainingItems[0].iconPath}
              />
            )}

            {/* Second stacked item */}
            {remainingItems[1] && (
              <BentoCard
                title={remainingItems[1].title}
                description={remainingItems[1].description}
                iconPath={remainingItems[1].iconPath}
              />
            )}
          </div>

          {/* Demo Section - takes up 2 columns */}
          {demoSection && (
            <div className="md:col-span-2 bg-gray-900 rounded-xl overflow-hidden p-8 md:p-12">
              {/* Title and button on the same line on desktop, stacked on mobile */}
              <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                <h5 className="text-left text-lg md:text-2xl lg:text-3xl font-bold text-white mb-4 md:mb-0">
                  {demoSection.title}
                </h5>
                {/* Hide button on mobile view */}
                <div className="hidden md:block">
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleLearnMoreClick}
                    className="inline-flex items-center text-white font-semibold bg-primary-600 hover:bg-primary-700 transition-colors px-5 py-2 rounded-md w-fit text-sm whitespace-nowrap md:ml-8"
                  >
                    {demoSection.ctaText} <ArrowRightIcon className="ml-2 h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Full width screenshot/video below */}
              <div className="w-full mb-6 md:mb-0 overflow-hidden">
                <video
                  ref={videoRef}
                  src="https://framerusercontent.com/assets/PacecR0aPw6Np96stkz6kfreGQ.mp4"
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster="https://framerusercontent.com/assets/PacecR0aPw6Np96stkz6kfreGQ.mp4?poster"
                  className="rounded-md shadow-lg w-full h-auto max-w-full"
                  style={{ objectFit: 'cover', backgroundColor: 'rgb(5, 5, 5)' }}
                />
              </div>

              {/* Show button below video only on mobile */}
              <div className="mt-6 flex justify-center md:hidden w-full">
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleLearnMoreClick}
                  className="inline-flex items-center justify-center text-white font-semibold bg-primary-600 hover:bg-primary-700 transition-colors px-5 py-3 rounded-md w-full text-sm"
                >
                  {demoSection.ctaText} <ArrowRightIcon className="ml-2 h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Bento8;